/** The order spine's layout (plan §2.4, §5): the registry and one order → the four stage
 *  panels in lifecycle order, each split into its Editable block, the "From <restech>"
 *  vendor block and the System block. Pure — the panels' FieldSlots read the draft
 *  themselves. A catalog key the registry does not place is not shown (the contract test
 *  fails CI first); a registry key the catalog lacks is skipped. */
import {
  ORDER_RECORD_STAGES,
  type OrderRecordCatalog,
  type OrderRecordEditContext,
  type OrderRecordStage,
} from '@/api/orderRecord';
import type { OrderDetail } from '@/api/orders';
import type { FieldBlockKind, RecordViewDef, ViewBlock, ViewFieldRef, ViewSection } from '@/features/records/views/types';

import { ORDER_STAGE_TITLES, type OrderRegistry } from '../registry';

/** What an order view lays out. */
export interface OrderRecordData {
  catalog: OrderRecordCatalog;
  editContext: OrderRecordEditContext;
  detail: OrderDetail;
}

/** The DOM id of a stage panel — the anchor the spine nodes and `1`–`4` target. */
export function orderStageAnchorId(stage: OrderRecordStage): string {
  return `order-stage-${stage}`;
}

/** Display name of whoever feeds the order ("Ventrata"); null when no integration
 *  created it. */
export function orderManagedBy(ctx: OrderRecordEditContext): string | null {
  const r = ctx.restech;
  return r.managed_by ? (r.managed_by_display ?? r.managed_by) : null;
}

const BLOCK_ORDER: FieldBlockKind[] = ['editable', 'vendor', 'system'];

export function orderSpineSections(
  registry: OrderRegistry,
  { catalog, editContext: ctx }: OrderRecordData,
): ViewSection<OrderRecordStage>[] {
  const placed = catalog.fields
    .filter((f) => registry[f.key] !== undefined)
    .sort((a, b) => registry[a.key]!.order - registry[b.key]!.order);
  const vendorLabel = `From ${orderManagedBy(ctx) ?? 'the booking system'}`;

  return ORDER_RECORD_STAGES.map((stage) => {
    const blocks: ViewBlock[] = [];
    for (const kind of BLOCK_ORDER) {
      const fields: ViewFieldRef[] = placed
        .filter((f) => registry[f.key]!.stage === stage && registry[f.key]!.block === kind)
        .map((f) => ({ key: f.key, entity: 'order', id: ctx.order_id, path: f.key }));
      if (fields.length === 0) continue;
      blocks.push({
        kind,
        ...(kind === 'vendor' ? { label: vendorLabel } : kind === 'system' ? { label: 'System' } : {}),
        fields,
      });
    }
    return { id: stage, title: ORDER_STAGE_TITLES[stage], blocks };
  });
}

export const ORDER_SPINE_VIEW: RecordViewDef<OrderRegistry, OrderRecordData, OrderRecordStage> = {
  id: 'spine',
  label: 'Spine',
  layout: orderSpineSections,
};
